import type { CarrierNumber, TwilioNumberPage } from "@voice/api-contract";

export type TwilioInventoryAccount = {
  id: string;
  label: string;
  items: CarrierNumber[];
  checkedAt: string;
};

type Api = <T>(path: string, options?: { signal?: AbortSignal }) => Promise<T>;

export async function loadTwilioInventory(api: Api, signal: AbortSignal) {
  const errors: string[] = [];
  let configured: boolean | null = null;
  try {
    signal.throwIfAborted();
    const result = await api<{ items: { id: string; label: string }[] }>(
      "/admin/carriers/twilio/accounts",
      { signal },
    );
    signal.throwIfAborted();
    configured = result.items.length > 0;
    const accounts = await Promise.all(
      result.items.map(async (account): Promise<TwilioInventoryAccount> => {
        const items: CarrierNumber[] = [];
        const seen = new Set<string>();
        let checkedAt = "";
        let page = 0;
        let pageToken: string | null = null;
        try {
          for (let index = 0; index < 100; index++) {
            signal.throwIfAborted();
            const query = new URLSearchParams({
              account: account.id,
              page: String(page),
            });
            if (pageToken !== null) query.set("pageToken", pageToken);
            const response = await api<TwilioNumberPage>(
              `/admin/carriers/twilio/numbers?${query}`,
              { signal },
            );
            signal.throwIfAborted();
            if (response.page !== page)
              throw new Error(
                "Inventory pagination changed. Refresh numbers to retry.",
              );
            if (items.length + response.items.length > 5000)
              throw new Error(
                "This account exceeds the 5,000-number display limit. The list is incomplete.",
              );
            for (const item of response.items) {
              if (seen.has(item.number))
                throw new Error(
                  "The carrier repeated a number across inventory pages. The list is incomplete; refresh to retry.",
                );
              seen.add(item.number);
              items.push(item);
            }
            if (response.nextPage === null) {
              checkedAt = response.checkedAt;
              return { ...account, items, checkedAt };
            }
            if (
              response.nextPage.page !== page + 1 ||
              !response.nextPage.pageToken ||
              response.nextPage.pageToken === pageToken
            )
              throw new Error(
                "Inventory pagination stopped unexpectedly. The list is incomplete.",
              );
            page = response.nextPage.page;
            pageToken = response.nextPage.pageToken;
          }
          throw new Error(
            "This account exceeds the inventory page limit. The list is incomplete.",
          );
        } catch (cause) {
          if (signal.aborted) throw cause;
          errors.push(`Twilio ${account.label}: ${(cause as Error).message}`);
        }
        return { ...account, items, checkedAt };
      }),
    );
    return { accounts, configured, errors };
  } catch (cause) {
    if (signal.aborted) throw cause;
    errors.push(`Twilio: ${(cause as Error).message}`);
    return { accounts: [], configured, errors };
  }
}
